import { useEffect, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Network, Plus, Trash2 } from 'lucide-react';
import {
  VLAN_PURPOSE_LABELS,
  vlanLabel,
  type OltVlan,
  type OnuPort,
  type OnuVlan,
  type Page,
  type VlanPurpose,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useTasks } from '../../lib/tasks';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Pill,
  Spinner,
  TBody,
  THead,
  TRow,
  Table,
} from '../../components/ui';
import { ConfigPlanCard } from './ConfigPlanCard';

const PURPOSE_TONE: Record<VlanPurpose, 'primary' | 'info' | 'warn' | 'success' | 'neutral'> = {
  internet: 'primary',
  voip: 'info',
  iptv: 'success',
  management: 'warn',
  other: 'neutral',
};

/**
 * VLANs carried to this ONU. Each one becomes a service port on the OLT, and
 * ports on the next tab reference them — which is why a VLAN cannot be removed
 * while a port still points at it.
 */
/**
 * `base` is the owner's API root — `/devices/<id>` for a subscriber whose ONU
 * has informed an ACS, `/onus/<id>` for one that has not. Both serve the same
 * endpoints, because which VLANs an ONU carries and which socket they come out
 * of is the same question either way; only the OLT-only case used to have
 * nowhere to answer it.
 */
/** What the API says about the push it made after saving. */
type Applied = { executed: boolean; errors: string[] } | null;

/**
 * Saving records the intent; the push is what makes it real. When the chassis
 * refuses, say so — a silent failure here is the platform and the OLT quietly
 * disagreeing about a live subscriber.
 */
function pushError(applied: Applied | undefined): string | null {
  if (!applied || applied.errors.length === 0) return null;
  return applied.executed
    ? `Saved, but the OLT rejected it: ${applied.errors[0]}`
    : `Saved, but it could not be sent to the OLT: ${applied.errors[0]}`;
}

/** `wifi_0/2` → "WiFi 2", `eth_0/1` → "LAN 1" — what the page calls a socket. */
function socketName(p: OnuPort): string {
  const m = /^(wifi|eth)_\d+\/(\d+)$/i.exec(p.port);
  const word = m ? `${m[1]!.toLowerCase() === 'wifi' ? 'WiFi' : 'LAN'} ${m[2]}` : p.port;
  return p.label && p.label !== word && !/^LAN \d+$/.test(p.label) ? `${word} (${p.label})` : word;
}

/** The sockets and SSIDs that still carry a VLAN, and whether each carries it alone. */
function carriersOf(ports: OnuPort[], vlanId: number): Array<{ port: OnuPort; only: boolean }> {
  const seen = new Set<string>();
  const out: Array<{ port: OnuPort; only: boolean }> = [];
  for (const p of ports) {
    if (p.pvid !== vlanId && !p.taggedVlans.includes(vlanId)) continue;
    if (seen.has(p.port)) continue;
    seen.add(p.port);
    const others = [p.pvid, ...p.taggedVlans].filter((v): v is number => v !== null && v !== vlanId);
    out.push({ port: p, only: others.length === 0 });
  }
  return out;
}

export function VlansTab({
  base,
  oltId,
  onuId,
}: {
  base: string;
  oltId?: string;
  onuId?: string;
}) {
  const tasks = useTasks();
  const { can } = useSession();
  // §8.6.2: the same gate PortsTab uses. `device.write` unconditionally showed
  // org_operator Attach/Detach controls the API answers 403 for an OLT-only ONU
  // (that write is `onu.provision`); mirroring by base keeps a third tab from
  // inventing a third answer.
  const editable = can(base.startsWith('/onus/') ? 'onu.provision' : 'device.write');

  const [showForm, setShowForm] = useState(false);
  /**
   * Attaching is a VLAN and nothing else.
   *
   * Name, purpose, mode and a service VLAN were all asked for here and none of
   * them is a decision at this moment: the name is the OLT's, the purpose is
   * whatever the VLAN is for on that chassis, and the mode belongs to the WAN
   * control where an operator goes looking for it. Four fields to answer one
   * question made attaching a VLAN feel like a form.
   */
  const [form, setForm] = useState({ vlanId: '' });
  /**
   * A detach that something still carries (operator, 2026-10-05: "keeps
   * saying attached to wifi 2 even though i disabled wifi 2"). Switching an
   * SSID off changes the router, not which VLAN its socket carries — so the
   * page asks, names the networks, and offers to unbind them in the same step.
   */
  const [unbindAsk, setUnbindAsk] = useState<{ vlan: OnuVlan; carriers: Array<{ port: OnuPort; only: boolean }> } | null>(null);
  const portsQuery = useQuery({
    queryKey: ['onu-config', base, 'ports'],
    queryFn: () => api.get<Page<OnuPort>>(`${base}/ports`),
  });

  const vlansQuery = useQuery({
    queryKey: ['onu-config', base, 'vlans'],
    queryFn: () => api.get<Page<OnuVlan>>(`${base}/vlans`),
  });

  // What may be attached: the VLANs this ONU's OLT actually carries, as listed
  // on that OLT's VLANs tab — same rows, same names, same classification.
  const availableQuery = useQuery({
    queryKey: ['onu-config', base, 'available-vlans'],
    queryFn: () =>
      api.get<{ items: OltVlan[]; note: string | null; vlan1Withheld?: boolean }>(
        `${base}/available-vlans`,
      ),
  });

  // Attaching and detaching a VLAN each push to the chassis, so — like resync
  // and the WAN wizards — they CLOSE on submit and run in the tasks tray rather
  // than pinning the tab to a spinner. A chassis that RECORDED the change but
  // refused to push it is not a success: `pushError` becomes a thrown message so
  // the tray marks the task failed and keeps it until acknowledged, which is the
  // one outcome that must not scroll past.
  const invalidateKeys = [
    ['onu-config', base, 'vlans'],
    ['onu-config', base, 'config-plan'],
  ];

  function attachVlan() {
    const vlanId = Number(form.vlanId);
    if (!vlanId) return;
    setShowForm(false);
    setForm({ vlanId: '' });
    tasks.start({
      label: `Attach VLAN ${vlanId}`,
      run: async () => {
        // Purpose and mode take their defaults — internet, and whichever the WAN
        // control says. Neither is a decision worth interrupting an attach for.
        const created = await api.post<OnuVlan & { applied?: Applied }>(`${base}/vlans`, { vlanId });
        const refused = pushError(created.applied);
        if (refused) throw new Error(refused);
        return created;
      },
      done: () => `VLAN ${vlanId} attached.`,
      invalidate: invalidateKeys,
    });
  }

  /**
   * Which subscriber VLAN is this ONU's — what the line test measures on and
   * what a swapped-in CPE is given. Bookkeeping only; nothing is written to
   * the chassis or the CPE, so it runs in the tray and settles at once.
   */
  function makePrimary(vlan: OnuVlan) {
    tasks.start({
      label: `Make VLAN ${vlan.vlanId} primary`,
      run: () => api.post(`${base}/vlans/${vlan.vlanId}/primary`, {}),
      done: () => `VLAN ${vlan.vlanId} is now primary.`,
      invalidate: invalidateKeys,
    });
  }

  function detachVlan(vlan: OnuVlan, opts: { unbind?: boolean } = {}) {
    if (!opts.unbind) {
      const carriers = carriersOf(portsQuery.data?.items ?? [], vlan.vlanId);
      if (carriers.length > 0) {
        setUnbindAsk({ vlan, carriers });
        return;
      }
    }
    setUnbindAsk(null);
    tasks.start({
      label: opts.unbind ? `Unbind and detach VLAN ${vlan.vlanId}` : `Detach VLAN ${vlan.vlanId}`,
      run: async () => {
        const r = await api.delete<{ applied?: Applied; unbound?: string[] }>(
          `${base}/vlans/${encodeURIComponent(vlan.id)}${opts.unbind ? '?unbindPorts=true' : ''}`,
        );
        const refused = pushError(r.applied);
        if (refused) throw new Error(refused);
        return r;
      },
      done: (r) =>
        r.unbound && r.unbound.length > 0
          ? `${r.unbound.join(', ')} taken off VLAN ${vlan.vlanId}, and the VLAN detached.`
          : `VLAN ${vlan.vlanId} detached.`,
      invalidate: [...invalidateKeys, ['onu-config', base, 'ports']],
    });
  }

  /**
   * The management VLAN is not shown here and cannot be attached to anything.
   *
   * It is the platform's own path to the ONU, not a service the subscriber
   * carries — it runs in the background and is changed from the Management
   * button on the ONU. Listing it beside the subscriber VLANs invited putting
   * it on a socket, which hands the customer the route we manage them by.
   */
  const vlans = (vlansQuery.data?.items ?? []).filter((v) => v.purpose !== 'management');
  /** Shown in their own card below — background plumbing, not a service. */
  const mgmtVlans = (vlansQuery.data?.items ?? []).filter((v) => v.purpose === 'management');

  /**
   * The form opens on a VLAN rather than on a prompt.
   *
   * With the "Select…" row gone the select shows its first option, so the form
   * has to actually hold it — otherwise Attach would post an empty VLAN while
   * an operator looked at a real one.
   */
  const attachable = (availableQuery.data?.items ?? []).filter(
    (v) => !vlans.some((attached) => attached.vlanId === v.vlanId),
  );
  useEffect(() => {
    if (form.vlanId || attachable.length === 0) return;
    setForm({ vlanId: String(attachable[0]!.vlanId) });
  }, [attachable, form.vlanId]);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    attachVlan();
  }

  const askShared = unbindAsk?.carriers.filter((c) => !c.only) ?? [];
  const unbindDialog = unbindAsk && (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={() => setUnbindAsk(null)} />
      <div className="relative w-full max-w-lg" role="dialog" aria-modal="true" aria-labelledby="unbind-title">
        <Card title={<span id="unbind-title">Detach VLAN {unbindAsk.vlan.vlanId}</span>}>
          <div className="space-y-3 p-5 text-sm">
            <p className="text-ink">
              VLAN {unbindAsk.vlan.vlanId} is still carried by{' '}
              <span className="font-medium">{unbindAsk.carriers.map((c) => socketName(c.port)).join(', ')}</span>.
              Switching a network off does not take it off the VLAN.
            </p>
            {askShared.length > 0 ? (
              <p className="text-warn">
                {askShared.map((c) => socketName(c.port)).join(', ')} also{' '}
                {askShared.length === 1 ? 'carries' : 'carry'} other VLANs, so {askShared.length === 1 ? 'it is' : 'they are'} not
                unbound from here. Change {askShared.length === 1 ? 'it' : 'them'} on the Ports tab first.
              </p>
            ) : (
              <p className="text-muted">
                Unbinding takes {unbindAsk.carriers.length === 1 ? 'it' : 'them'} off VLAN {unbindAsk.vlan.vlanId} on the
                OLT as well, then detaches the VLAN.
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setUnbindAsk(null)}>
                Cancel
              </Button>
              {askShared.length === 0 && (
                <Button
                  variant="danger"
                  icon={<Trash2 className="size-3.5" />}
                  onClick={() => detachVlan(unbindAsk.vlan, { unbind: true })}
                >
                  Unbind and detach
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {unbindDialog}
      <Card
        title={
          <span className="flex items-center gap-2">
            <Network className="size-4 text-muted" />
            Attached VLANs
            <span className="text-xs font-normal text-faint">{vlans.length}</span>
          </span>
        }
        action={
          editable ? (
            <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setShowForm((v) => !v)}>
              Attach VLAN
            </Button>
          ) : null
        }
      >
        {showForm && (
          <form onSubmit={onSubmit} className="grid gap-3 border-b border-line p-5 sm:max-w-md">
            <Field
              label="VLAN"
              hint={
                /* A VLAN the OLT carries but the tenant does not offer is not
                   the same as a VLAN the OLT has not got, and the operator can
                   only tell the two apart if this says so. */
                (availableQuery.data?.items.length ?? 0) > 0
                  ? availableQuery.data?.vlan1Withheld
                    ? "From the OLT's uplink. VLAN 1 is carried here but is not offered to subscribers for this organization."
                    : "From the OLT's uplink"
                  : (availableQuery.data?.note ?? undefined)
              }
            >
              {(availableQuery.data?.items.length ?? 0) > 0 ? (
                <select
                  className="input"
                  value={form.vlanId}
                  onChange={(e) => setForm({ ...form, vlanId: e.target.value })}
                  required
                  autoFocus
                >
                  {/* Same "<id> - <description>" label the OLT VLANs tab uses,
                      so a VLAN reads identically wherever it is offered. No
                      "Select…" row: every entry here is a VLAN the OLT carries,
                      and the first is as good a starting point as any. */}
                  {attachable.map((v) => (
                    <option key={v.id} value={String(v.vlanId)}>
                      {vlanLabel(v)}
                      {v.usedForMgmt ? ' · mgmt' : v.usedForIptv ? ' · iptv' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  value={form.vlanId}
                  onChange={(e) => setForm({ ...form, vlanId: e.target.value })}
                  placeholder="5"
                  type="number"
                  min={1}
                  max={4094}
                  required
                  autoFocus
                />
              )}
            </Field>
            <div className="flex gap-2">
              <Button type="submit" variant="primary" size="sm">
                Attach
              </Button>
              <Button type="button" size="sm" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        )}

        {vlansQuery.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : vlans.length === 0 ? (
          <EmptyState
            icon={<Network className="size-8" />}
            title="No VLANs attached"
            description="Attach the VLANs this ONU carries, then assign them to ports on the Ports tab."
          />
        ) : (
          <Table>
            <THead>
              <th className="th">VLAN</th>
              <th className="th">Role</th>
              <th className="th">Purpose</th>
              <th className="th">Mode</th>
              <th className="th">Service VLAN</th>
              <th className="th" />
            </THead>
            <TBody>
              {vlans.map((vlan) => (
                <TRow key={vlan.id}>
                  {/* "12 - ftth hs": the id and the OLT's name for it, the same
                      label the attach picker above and the bulk forms use
                      (operator, 2026-09-24). Rename it on the OLT's VLANs tab and
                      it is renamed here. */}
                  <td className="td">{vlanLabel({ vlanId: vlan.vlanId, description: vlan.label })}</td>
                  <td className="td">
                    {vlan.role === 'primary' ? (
                      <Pill tone="success">Primary</Pill>
                    ) : vlan.role === 'secondary' ? (
                      <span className="flex items-center gap-2">
                        <Pill tone="neutral">Secondary</Pill>
                        {editable && (
                          <Button size="sm" variant="ghost" onClick={() => makePrimary(vlan)}>
                            Make primary
                          </Button>
                        )}
                      </span>
                    ) : (
                      <span className="text-faint">—</span>
                    )}
                  </td>
                  <td className="td">
                    <Pill state={false} tone={PURPOSE_TONE[vlan.purpose]}>
                      {VLAN_PURPOSE_LABELS[vlan.purpose]}
                    </Pill>
                  </td>
                  <td className="td">
                    {/* Not the same question as the port's access/trunk/hybrid,
                        which is about tags. This is who terminates the
                        connection. */}
                    <Pill tone={vlan.mode === 'route' ? 'info' : 'neutral'}>
                      {vlan.mode === 'route' ? 'Routed' : 'Bridged'}
                    </Pill>
                  </td>
                  <td className="td font-mono text-muted">
                    {vlan.serviceVlanId ?? <span className="text-faint">same</span>}
                  </td>
                  <td className="td text-right">
                    {editable && (
                      <Button
                        size="sm"
                        variant="danger"
                        icon={<Trash2 className="size-3.5" />}
                        onClick={() => detachVlan(vlan)}
                      >
                        Detach
                      </Button>
                    )}
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <Card
        title={
          <span className="flex items-center gap-2">
            <Network className="size-4 text-muted" />
            Management VLAN
          </span>
        }
      >
        {/*
          Deliberately its own card rather than a row above: the management
          VLAN is the platform’s path to the ONU, runs in the background, and
          is changed from the Management control — listing it beside the
          subscriber VLANs invited putting it on a socket, which hands the
          customer the route we manage them by.
        */}
        <div className="p-5">
          {mgmtVlans.length > 0 ? (
            <div className="flex flex-wrap items-center gap-3">
              {mgmtVlans.map((v) => (
                <span key={v.id} className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-ink">
                    {vlanLabel({ vlanId: v.vlanId, description: v.label })}
                  </span>
                  <Pill tone="warn">management</Pill>
                </span>
              ))}
              <span className="text-xs text-faint">
                Set from the Management control; carried to the ONU as its own address, never on a
                socket.
              </span>
            </div>
          ) : (
            <p className="text-sm text-muted">
              None recorded yet. It is adopted from the chassis when this ONU’s configuration is
              read — open its Settings, or press refresh there — and set from the Management
              control.
            </p>
          )}
        </div>
      </Card>

      <ConfigPlanCard base={base} oltId={oltId} onuId={onuId} />
    </div>
  );
}
